import { memo } from 'react';
import { t } from 'i18next';
import { Flow } from '@thive/fddm-types';

import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { useOrderProfileStore } from '@/modules/orderProfile/store/useOrderProfileStore';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';

import { useFlowUpdaters } from '../hooks/useFlowUpdaters';
import { FlowGraph, LoadPerHourInput } from '.';

interface Props {
  id: string;
  disabled: boolean;
  flow: Flow;
}

function ConsolidationRetrievalProperties({ id, disabled, flow }: Props) {
  const { hourlyOrderCounts, consolidationRetrievalExtension, vehicleLimit } = flow;
  const {
    onFlowVehicleLimitChange,
    onShipmentSizeChange,
    onConsolidationGroupsChange,
    onRetrievalStartDelayChange,
  } = useFlowUpdaters(id);
  const { orderProfile } = useOrderProfileStore();
  const totalNumLoads = hourlyOrderCounts[0];

  if (!consolidationRetrievalExtension) return null;

  const { shipmentSize, numConsolidations, retrievalStartDelayInHours } =
    consolidationRetrievalExtension;

  return (
    <>
      {orderProfile ? (
        <FlowGraph hourlyOrderCounts={hourlyOrderCounts} />
      ) : (
        <PropertiesLabel
          labelRatio={70}
          i18nkey='interface:properties.flows.flow_info.total_loads.label'
        >
          <LoadPerHourInput id={id} totalNumLoads={totalNumLoads} disabled={disabled} />
        </PropertiesLabel>
      )}
      <PropertiesLabel
        labelRatio={70}
        i18nkey='interface:properties.flows.consolidation.fields.shipment_size.label'
      >
        <NumberInput value={shipmentSize} minValue={1} onChange={onShipmentSizeChange} />
      </PropertiesLabel>
      <PropertiesLabel
        labelRatio={70}
        i18nkey='interface:properties.flows.consolidation.fields.consolidation_groups.label'
      >
        <NumberInput
          value={numConsolidations}
          minValue={1}
          minValueErrorMsg={t('common:minimum_value', { minValue: 1 })}
          onChange={onConsolidationGroupsChange}
        />
      </PropertiesLabel>
      <PropertiesLabel
        labelRatio={70}
        i18nkey='interface:properties.flows.consolidation.fields.retrieval_start_delay.label'
      >
        <NumberInput
          value={retrievalStartDelayInHours}
          unit={t(`interface:settings.units.hours`)}
          minValue={1}
          minValueErrorMsg={t('common:minimum_value', { minValue: 1 })}
          onChange={onRetrievalStartDelayChange}
        />
      </PropertiesLabel>
      <PropertiesLabel labelRatio={70} skipFocus i18nkey='interface:properties.highway.vehicles'>
          <NumberInput
            value={vehicleLimit}
            onChange={onFlowVehicleLimitChange}
            clearButton={{
              text: 'All',
              value: -1,
            }}
            sx={{ width: '100%', float: 'right' }}
            disabled={disabled}
          />
      </PropertiesLabel>
    </>
  );
}

export default memo(ConsolidationRetrievalProperties);
